import theme from '@app/theme';
import { DateTimePicker } from '@components/date/DateTimePicker';
import { PopupProps } from '@progress/kendo-react-popup';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

const Styles = styled.div.attrs((props) => ({ className: 'app-date-time-range-picker' }))`
    &&&.app-date-time-range-picker {
        display: flex;
        gap: 0.28em;
        align-items: center;

        .divider {
            border-bottom: 1px solid ${theme.colors.base_grey};
            width: 0.71em;
            height: 0;
        }
    }
`;

type DateTimeRangePickerProps = {
    from?: Date;
    to?: Date;
    onChange?: (from: Date | undefined, to: Date | undefined) => any;
    className?: string;
};

export const DateTimeRangePicker = styled((props: DateTimeRangePickerProps) => {
    const { from: externalFrom, to: externalTo, onChange, className } = props;

    const [from, setFrom] = useState(() => externalFrom);
    const [to, setTo] = useState(() => externalTo || from);

    const fromTriggerRef = useRef<HTMLElement>();

    function handleFromChange(e) {
        setFrom(e.value);
        notifyChange(e.value, to);
    }

    function handleToChange(e) {
        setTo(e.value);
        notifyChange(from, e.value);
    }

    function notifyChange(from, to) {
        if (onChange) {
            onChange(from, to);
        }
    }

    useEffect(() => {
        setFrom(externalFrom);
        setTo((prevTo) => {
            const to = externalFrom && prevTo && prevTo < externalFrom ? externalFrom : prevTo;
            Promise.resolve().then(() => notifyChange(externalFrom, to));
            return to;
        });
    }, [externalFrom]);

    useEffect(() => {
        setTo(externalTo);
        setFrom((prevFrom) => {
            const from = externalTo && prevFrom && prevFrom > externalTo ? externalTo : prevFrom;
            Promise.resolve().then(() => notifyChange(from, externalTo));
            return from;
        });
    }, [externalTo]);

    const [toPopupSettings, setToPopupSettings] = useState<PopupProps>({});
    useEffect(() => {
        setToPopupSettings({
            anchor: fromTriggerRef.current,
        });
    }, [fromTriggerRef.current]);

    return (
        <Styles className={`app-date-time-range-picker ${className ?? ''}`}>
            <DateTimePicker
                value={from}
                onChange={handleFromChange}
                max={to}
                placeholder="From Date"
                triggerRef={fromTriggerRef}
            />

            <div className="divider" />

            <DateTimePicker
                value={to}
                onChange={handleToChange}
                min={from}
                placeholder="To Date"
                popupSettings={toPopupSettings}
            />
        </Styles>
    );
})``;
